// src/services/firebase.js
import {
  mockAuthService,
  mockUsersService,
  mockProceduresService,
  mockAppointmentsService,
  mockNotificationsService,
  mockRescheduleService,
  mockScheduleWindowsService,
  mockServerTimestamp,
} from 'infrastructure/mockAuth';
import { sanitizeData } from 'shared/utils/security';

// Wrapper: sanitiza dados antes de qualquer escrita no Firestore
const safe = (data) => sanitizeData(data);

// ─── MOCK MODE ───────────────────────────────────────────────────────────────
// false = usa Firebase real; true = usa dados mock locais
const USE_MOCK = false;
// ─────────────────────────────────────────────────────────────────────────────

// Nomes das coleções no Firestore (em português, conforme criadas no console)
const COL = {
  users:            'usuarios',
  procedures:       'procedimentos',
  appointments:     'agendamentos',
  notifications:    'notificacoes',
  reschedule:       'reagendamentos',
  scheduleWindows:  'janelaAgenda',
  establishments:   'estabelecimentos',   // multi-tenant: clínicas/salões
  chats:            'chats',              // chat cliente ↔ profissional
  payments:         'pagamentos',         // estrutura preparatória de pagamentos
};

let app, auth, db, realServerTimestamp;

if (!USE_MOCK) {
  const { initializeApp, getApps } = require('firebase/app');
  const { getAuth }                = require('firebase/auth');
  const { getFirestore, serverTimestamp: fbServerTimestamp } = require('firebase/firestore');

  const firebaseConfig = {
    apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  };

  app  = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  auth = getAuth(app);
  db   = getFirestore(app);
  realServerTimestamp = fbServerTimestamp;
}

// ─── AUTH SERVICE ─────────────────────────────────────────────────────────────
export const authService = USE_MOCK ? mockAuthService : (function() {
  const {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
    sendPasswordResetEmail,
    reauthenticateWithCredential,
    updatePassword,
    EmailAuthProvider,
    deleteUser,
  } = require('firebase/auth');
  const { initializeApp, deleteApp, getApps } = require('firebase/app');
  const { getAuth: getSecondaryAuth }         = require('firebase/auth');
  const { doc, setDoc, getDoc, getFirestore } = require('firebase/firestore');

  const googleProvider = new GoogleAuthProvider();
  googleProvider.setCustomParameters({ prompt: 'select_account' });

  const firebaseConfig = {
    apiKey:            process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain:        process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId:         process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket:     process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId:             process.env.REACT_APP_FIREBASE_APP_ID,
  };

  return {
    onAuthChange: (cb) => onAuthStateChanged(auth, cb),

    login: (email, password) => signInWithEmailAndPassword(auth, email, password),

    loginWithGoogle: async () => {
      const createDocIfNeeded = async (user) => {
        const userRef = doc(db, COL.users, user.uid);
        const snap    = await getDoc(userRef);
        if (!snap.exists()) {
          await setDoc(userRef, safe({
            uid:         user.uid,
            name:        user.displayName || 'Usuário Google',
            email:       user.email,
            role:        'client',
            phone:       '',
            specialty:   '',
            active:      true,
            avatarColor: 'blue',
            createdAt:   realServerTimestamp(),
          }));
        }
      };

      const startGoogleRedirect = async () => {
        sessionStorage.setItem('googleRedirectPending', '1');
        await signInWithRedirect(auth, googleProvider);
        return null;
      };

      // Popup é o fluxo recomendado para Firebase SDK v10+ em todos os ambientes.
      // signInWithRedirect falha em Safari/iOS e Chrome com cookies particionados
      // porque o getRedirectResult() depende de cookies cross-site bloqueados por padrão.
      try {
        const cred = await signInWithPopup(auth, googleProvider);
        await createDocIfNeeded(cred.user);
        return cred.user;
      } catch (err) {
        const code = err?.code || '';
        // Apenas erros de popup cancelado/fechado pelo usuário são silenciosos.
        // auth/internal-error e auth/network-request-failed indicam problemas de
        // configuração OAuth ou rede — o redirect também falharia pelos mesmos motivos.
        const silentCodes = [
          'auth/popup-closed-by-user',
          'auth/cancelled-popup-request',
        ];
        // Popup bloqueado pelo browser → fallback para redirect
        if (code === 'auth/popup-blocked') {
          console.warn('[Google Auth] Popup bloqueado, tentando redirect:', code);
          return startGoogleRedirect();
        }
        if (silentCodes.includes(code)) {
          return null; // usuário cancelou, não é erro
        }
        throw err;
      }
    },

    // Chamado no carregamento do app para capturar resultado do redirect Google
    handleGoogleRedirect: async () => {
      try {
        const result = await getRedirectResult(auth);
        sessionStorage.removeItem('googleRedirectPending');
        sessionStorage.removeItem('googleAuthError');

        if (!result?.user) return null;

        const userRef = doc(db, COL.users, result.user.uid);
        const snap    = await getDoc(userRef);
        if (!snap.exists()) {
          await setDoc(userRef, safe({
            uid:         result.user.uid,
            name:        result.user.displayName || 'Usuário Google',
            email:       result.user.email,
            role:        'client',
            phone:       '',
            specialty:   '',
            active:      true,
            avatarColor: 'blue',
            createdAt:   realServerTimestamp(),
          }));
        }
        return result.user;
      } catch (e) {
        sessionStorage.removeItem('googleRedirectPending');
        const payload = { code: e?.code || 'unknown', message: e?.message || 'Erro Google redirect' };
        sessionStorage.setItem('googleAuthError', JSON.stringify(payload));
        console.error('[Google Redirect] code:', e?.code || 'unknown');
        throw e;
      }
    },

    logout: () => signOut(auth),

    // Deleta o usuário autenticado no momento (usado para rollback de cadastro com falha).
    // Após deletar, o Firebase já remove a sessão; faz signOut por segurança.
    deleteCurrentUser: async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        await deleteUser(user);
      } catch (e) {
        // Se exigir reautenticação recente, ao menos desloga para não deixar sessão órfã
        await signOut(auth).catch(() => {});
        throw e;
      }
    },

    resetPassword: (email) => sendPasswordResetEmail(auth, email),

    changePassword: async (currentPassword, newPassword) => {
      const user = auth.currentUser;
      if (!user || !user.email) throw new Error('Usuário não autenticado.');
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
    },

    // Usa instância secundária para não deslogar o admin ao criar novo usuário
    register: async ({
      email, password, name, role, phone = '', specialty = '',
      cpf = '', crm = '', cro = '', estabelecimentoId = null,
      subscriptionStatus, accessExpiresAt,
    }) => {
      const SECONDARY = 'agenda-secondary';
      const existing  = getApps().find(a => a.name === SECONDARY);
      const secondary = existing || initializeApp(firebaseConfig, SECONDARY);
      const secAuth   = getSecondaryAuth(secondary);
      const secDb     = getFirestore(secondary);
      try {
        const cred = await createUserWithEmailAndPassword(secAuth, email, password);

        // Status padrão de assinatura: TRIAL de 30 dias para novos usuários
        // (clientes não têm controle de assinatura — apenas admin e professional)
        const trialExpiry = new Date();
        trialExpiry.setDate(trialExpiry.getDate() + 30);
        trialExpiry.setHours(23, 59, 59, 999);

        const profile = {
          uid:                cred.user.uid,
          name,
          email,
          role,
          phone,
          specialty,
          active:             true,
          avatarColor:        ['red','blue','green','gold','purple'][Math.floor(Math.random() * 5)],
          createdAt:          realServerTimestamp(),
          subscriptionStatus: subscriptionStatus ?? (role === 'client' ? null : 'TRIAL'),
          accessExpiresAt:    accessExpiresAt   ?? (role === 'client' ? null : trialExpiry),
        };
        if (cpf)               profile.cpf               = cpf;
        if (crm)               profile.crm               = crm;
        if (cro)               profile.cro               = cro;
        if (estabelecimentoId) profile.estabelecimentoId = estabelecimentoId;

        // ROLLBACK ATÔMICO: o Auth já foi criado acima. Se a gravação do perfil no
        // Firestore falhar, removemos o usuário Auth recém-criado para NÃO deixar uma
        // conta órfã (Auth existe, perfil ausente) — estado que já permitiu acesso
        // indevido quando o sistema assumia um role padrão para usuário sem perfil.
        try {
          await setDoc(doc(secDb, COL.users, cred.user.uid), safe(profile));
        } catch (writeErr) {
          await deleteUser(secAuth.currentUser).catch(() => {});
          await signOut(secAuth).catch(() => {});
          throw writeErr;
        }
        await signOut(secAuth);
        return cred.user;
      } finally {
        if (!existing) await deleteApp(secondary);
      }
    },
  };
})();

// ─── USERS SERVICE ────────────────────────────────────────────────────────────
export const usersService = USE_MOCK ? mockUsersService : (function() {
  const {
    collection, doc, setDoc, getDoc, getDocs,
    updateDoc, deleteDoc, query, where, onSnapshot,
  } = require('firebase/firestore');

  return {
    create: (uid, data) =>
      setDoc(doc(db, COL.users, uid), safe({
        uid, ...data, active: true, createdAt: realServerTimestamp(),
      })),

    getById: async (uid) => {
      const snap = await getDoc(doc(db, COL.users, uid));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    },

    update: (uid, data) => updateDoc(doc(db, COL.users, uid), safe(data)),

    delete: (uid) => deleteDoc(doc(db, COL.users, uid)),

    onSnapshot: (cb, role, onError) => {
      // Sem orderBy no Firestore — evita necessidade de índice composto
      const constraints = role ? [where('role', '==', role)] : [];
      return onSnapshot(
        query(collection(db, COL.users), ...constraints),
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
          cb(list);
        },
        onError || ((e) => console.error('[Firestore/usuarios]', e))
      );
    },

    getByRole: async (role) => {
      const snap = await getDocs(
        query(collection(db, COL.users), where('role', '==', role))
      );
      return snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
    },

    getByEmail: async (email) => {
      const snap = await getDocs(
        query(collection(db, COL.users), where('email', '==', email.toLowerCase()))
      );
      if (snap.empty) return null;
      const d = snap.docs[0];
      return { id: d.id, ...d.data() };
    },

    // Atualiza subscriptionStatus e accessExpiresAt num único update
    setSubscription: (uid, status, expiresAt) =>
      updateDoc(doc(db, COL.users, uid), {
        subscriptionStatus: status,
        accessExpiresAt: expiresAt ?? null,
      }),
  };
})();

// ─── PROCEDURES SERVICE ───────────────────────────────────────────────────────
export const proceduresService = USE_MOCK ? mockProceduresService : (function() {
  const {
    collection, doc, addDoc, getDocs, updateDoc, deleteDoc, onSnapshot, query, where,
  } = require('firebase/firestore');

  return {
    add: (data) =>
      addDoc(collection(db, COL.procedures), safe({
        ...data,
        approved:  data.approved ?? false,
        createdAt: realServerTimestamp(),
      })),

    create: (data) => proceduresService.add(data),

    update: (id, data) => updateDoc(doc(db, COL.procedures, id), safe(data)),

    delete: (id) => deleteDoc(doc(db, COL.procedures, id)),

    reject: (id) => deleteDoc(doc(db, COL.procedures, id)),

    approve: (id) =>
      updateDoc(doc(db, COL.procedures, id), {
        approved:   true,
        approvedAt: realServerTimestamp(),
      }),

    onSnapshot: (cb, onError) =>
      onSnapshot(
        collection(db, COL.procedures),
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          list.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
          cb(list);
        },
        onError || ((e) => console.error('[Firestore/procedimentos]', e))
      ),

    getByName: async (name) => {
      const snap = await getDocs(
        query(collection(db, COL.procedures), where('name', '==', name))
      );
      if (snap.empty) return null;
      return { id: snap.docs[0].id, ...snap.docs[0].data() };
    },
  };
})();

// ─── APPOINTMENTS SERVICE ─────────────────────────────────────────────────────
export const appointmentsService = USE_MOCK ? mockAppointmentsService : (function() {
  const {
    collection, doc, addDoc, updateDoc, deleteDoc,
    query, where, onSnapshot, getDocs,
  } = require('firebase/firestore');

  return {
    add: (data) =>
      addDoc(collection(db, COL.appointments), safe({
        ...data,
        status:    'pending',
        done:      false,
        createdAt: realServerTimestamp(),
      })),

    update: (id, data) => updateDoc(doc(db, COL.appointments, id), safe(data)),

    delete: (id) => deleteDoc(doc(db, COL.appointments, id)),

    confirm: (id) =>
      updateDoc(doc(db, COL.appointments, id), { status: 'confirmed' }),

    cancel: (id) =>
      updateDoc(doc(db, COL.appointments, id), {
        status:      'cancelled',
        cancelledAt: realServerTimestamp(),
      }),

    markDone: (id) =>
      updateDoc(doc(db, COL.appointments, id), {
        done:   true,
        doneAt: realServerTimestamp(),
      }),

    setWaitingConfirm: (id) =>
      updateDoc(doc(db, COL.appointments, id), { status: 'waitingConfirm' }),

    onSnapshot: (cb, filters = {}, onError) => {
      // Apenas filtros de igualdade — sem orderBy composto (evita índice)
      const constraints = [];
      if (filters.estabelecimentoId)
        constraints.push(where('estabelecimentoId', '==', filters.estabelecimentoId));
      if (filters.professionalId)
        constraints.push(where('professionalId', '==', filters.professionalId));
      if (filters.clientId)
        constraints.push(where('clientId', '==', filters.clientId));
      if (filters.date)
        constraints.push(where('date', '==', filters.date));
      if (filters.status)
        constraints.push(where('status', '==', filters.status));
      return onSnapshot(
        query(collection(db, COL.appointments), ...constraints),
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          // Ordenação client-side: data asc, horário asc
          list.sort((a, b) =>
            (a.date || '').localeCompare(b.date || '') ||
            (a.time || '').localeCompare(b.time || '')
          );
          cb(list);
        },
        onError || ((e) => console.error('[Firestore/agendamentos]', e))
      );
    },

    getOccupiedSlots: async (professionalId, date) => {
      // Apenas 2 filtros de igualdade = sem índice composto necessário
      // Filtragem de status é feita client-side
      const snap = await getDocs(
        query(
          collection(db, COL.appointments),
          where('professionalId', '==', professionalId),
          where('date',           '==', date)
        )
      );
      const ACTIVE = new Set(['pending', 'confirmed', 'waitingConfirm']);
      return snap.docs
        .map(d => d.data())
        .filter(a => ACTIVE.has(a.status))
        .map(a => a.time);
    },
  };
})();

// ─── NOTIFICATIONS SERVICE ────────────────────────────────────────────────────
export const notificationsService = USE_MOCK ? mockNotificationsService : (function() {
  const {
    collection, doc, addDoc, updateDoc, query, where, onSnapshot,
  } = require('firebase/firestore');

  return {
    add: (data) =>
      addDoc(collection(db, COL.notifications), safe({
        ...data,
        read:      false,
        createdAt: realServerTimestamp(),
      })),

    dismiss: (id) =>
      updateDoc(doc(db, COL.notifications, id), {
        read:   true,
        readAt: realServerTimestamp(),
      }),

    onSnapshotByClient: (userId, cb, onError) =>
      onSnapshot(
        query(
          collection(db, COL.notifications),
          where('userId', '==', userId),
          where('read',   '==', false)
          // sem orderBy — evita índice composto; ordenamos client-side
        ),
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          list.sort((a, b) => {
            const ta = a.createdAt?.toMillis?.() || 0;
            const tb = b.createdAt?.toMillis?.() || 0;
            return tb - ta; // desc
          });
          cb(list);
        },
        onError || ((e) => console.error('[Firestore/notificacoes]', e))
      ),
  };
})();

// ─── RESCHEDULE SERVICE ───────────────────────────────────────────────────────
export const rescheduleService = USE_MOCK ? mockRescheduleService : (function() {
  const {
    collection, doc, addDoc, updateDoc, query, where, onSnapshot,
  } = require('firebase/firestore');

  return {
    request: (data) =>
      addDoc(collection(db, COL.reschedule), safe({
        ...data,
        status:    'pending',
        createdAt: realServerTimestamp(),
      })),

    accept: async (id, appointmentId, newDate, newTime) => {
      await Promise.all([
        updateDoc(doc(db, COL.reschedule,   id),            { status: 'accepted', resolvedAt: realServerTimestamp() }),
        updateDoc(doc(db, COL.appointments, appointmentId), { date: newDate, time: newTime, status: 'confirmed' }),
      ]);
    },

    reject: (id) =>
      updateDoc(doc(db, COL.reschedule, id), {
        status:     'rejected',
        resolvedAt: realServerTimestamp(),
      }),

    onSnapshotByClient: (clientId, cb) =>
      onSnapshot(
        query(
          collection(db, COL.reschedule),
          where('clientId', '==', clientId),
          where('status',   '==', 'pending')
        ),
        (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
      ),
  };
})();

// ─── SCHEDULE WINDOWS SERVICE ─────────────────────────────────────────────────
export const scheduleWindowsService = USE_MOCK ? mockScheduleWindowsService : (function() {
  const {
    collection, doc, addDoc, updateDoc, deleteDoc, getDocs, onSnapshot, query, where,
  } = require('firebase/firestore');

  return {
    add: (data) =>
      addDoc(collection(db, COL.scheduleWindows), safe({
        ...data,
        createdAt: realServerTimestamp(),
      })),

    update: (id, data) => updateDoc(doc(db, COL.scheduleWindows, id), safe(data)),

    delete: (id) => deleteDoc(doc(db, COL.scheduleWindows, id)),

    // Deleta todas as janelas de um profissional específico (útil para limpar dados de teste)
    deleteByProfessional: async (professionalId) => {
      const snap = await getDocs(
        query(collection(db, COL.scheduleWindows), where('professionalId', '==', professionalId))
      );
      await Promise.all(snap.docs.map(d => deleteDoc(doc(db, COL.scheduleWindows, d.id))));
      return snap.docs.length;
    },

    approve: (id, adminId) =>
      updateDoc(doc(db, COL.scheduleWindows, id), {
        status:     'approved',
        approvedBy: adminId,
        approvedAt: realServerTimestamp(),
      }),

    reject: (id) =>
      updateDoc(doc(db, COL.scheduleWindows, id), { status: 'rejected' }),

    onSnapshot: (cb, onError) =>
      onSnapshot(
        collection(db, COL.scheduleWindows),
        (snap) => {
          const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          list.sort((a, b) =>
            (a.date || '').localeCompare(b.date || '') ||
            (a.startTime || '').localeCompare(b.startTime || '')
          );
          cb(list);
        },
        onError || ((e) => console.error('[Firestore/janelaAgenda]', e))
      ),
  };
})();

export const serverTimestamp = USE_MOCK ? mockServerTimestamp : realServerTimestamp;

// ─── BUSINESS CONFIG SERVICE ──────────────────────────────────────────────────
// Configurações globais do negócio: dias de funcionamento, horário, etc.
export const DEFAULT_BUSINESS_CONFIG = {
  // Dias da semana que o estabelecimento funciona (0=Dom, 1=Seg … 6=Sáb)
  // Padrão: segunda a sábado (sem domingo)
  workDays:  [1, 2, 3, 4, 5, 6],
  workStart: '08:00',
  workEnd:   '18:00',
  name:      'Meu Estabelecimento',
};

export const businessConfigService = USE_MOCK
  ? {
      get: async () => ({ id: 'config', ...DEFAULT_BUSINESS_CONFIG }),
      set: async (data) => { console.log('[Mock] businessConfig.set:', data); },
    }
  : {
      get: async () => {
        const { doc: fsDoc, getDoc } = require('firebase/firestore');
        try {
          const snap = await getDoc(fsDoc(db, 'configuracoes', 'negocio'));
          return snap.exists()
            ? { id: snap.id, ...snap.data() }
            : { id: 'negocio', ...DEFAULT_BUSINESS_CONFIG };
        } catch {
          return { id: 'negocio', ...DEFAULT_BUSINESS_CONFIG };
        }
      },
      set: async (data) => {
        const { doc: fsDoc, setDoc: fsSetDoc } = require('firebase/firestore');
        return fsSetDoc(fsDoc(db, 'configuracoes', 'negocio'), safe(data), { merge: true });
      },
    };

// ─── ESTABLISHMENTS SERVICE ───────────────────────────────────────────────────
export const establishmentsService = USE_MOCK
  ? {
      add:        async (data) => ({ id: 'mock-estab-1', ...data }),
      getById:    async ()     => null,
      update:     async ()     => {},
      delete:     async ()     => {},
      getAll:     async ()     => [],
      onSnapshot: ()           => () => {},
    }
  : (function() {
      const {
        collection, doc, addDoc, setDoc, getDoc, getDocs, updateDoc, deleteDoc, onSnapshot, query, where,
      } = require('firebase/firestore');

      return {
        add: (data) =>
          addDoc(collection(db, COL.establishments), safe({
            ...data,
            ativo:     true,
            createdAt: realServerTimestamp(),
          })),

        // Gera um ID de documento sem gravar nada (usado no cadastro de clínica para
        // vincular o admin ao estabelecimento ANTES de criar a sessão, evitando o
        // estado transitório "admin sem estabelecimento" = super admin).
        newId: () => doc(collection(db, COL.establishments)).id,

        // Cria o estabelecimento com um ID pré-gerado
        addWithId: (id, data) =>
          setDoc(doc(db, COL.establishments, id), safe({
            ...data,
            ativo:     true,
            createdAt: realServerTimestamp(),
          })),

        getById: async (id) => {
          const snap = await getDoc(doc(db, COL.establishments, id));
          return snap.exists() ? { id: snap.id, ...snap.data() } : null;
        },

        update: (id, data) => updateDoc(doc(db, COL.establishments, id), safe(data)),

        delete: (id) => deleteDoc(doc(db, COL.establishments, id)),

        getAll: async () => {
          const snap = await getDocs(collection(db, COL.establishments));
          return snap.docs
            .map(d => ({ id: d.id, ...d.data() }))
            .sort((a, b) => (a.nomeFantasia || '').localeCompare(b.nomeFantasia || '', 'pt-BR'));
        },

        getByNomeFantasia: async (nome) => {
          const snap = await getDocs(
            query(collection(db, COL.establishments), where('nomeFantasia', '==', nome))
          );
          if (snap.empty) return null;
          return { id: snap.docs[0].id, ...snap.docs[0].data() };
        },

        onSnapshot: (cb, onError) =>
          onSnapshot(
            collection(db, COL.establishments),
            (snap) => {
              const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
              list.sort((a, b) => (a.nomeFantasia || '').localeCompare(b.nomeFantasia || '', 'pt-BR'));
              cb(list);
            },
            onError || ((e) => console.error('[Firestore/estabelecimentos]', e))
          ),
      };
    })();

// ─── CHATS SERVICE ────────────────────────────────────────────────────────────
export const chatsService = USE_MOCK
  ? {
      createOrGet:      async () => ({ id: 'mock-chat-1' }),
      sendMessage:      async () => {},
      onSnapshotByAppt: ()       => () => {},
      onSnapshotMsgs:   ()       => () => {},
    }
  : (function() {
      const {
        collection, doc, addDoc, getDocs, setDoc, query, where, onSnapshot, orderBy,
      } = require('firebase/firestore');

      return {
        // Cria ou retorna chat existente vinculado ao agendamento
        createOrGet: async ({ appointmentId, clientId, professionalId }) => {
          const q    = query(collection(db, COL.chats), where('appointmentId', '==', appointmentId));
          const snap = await getDocs(q);
          if (!snap.empty) return { id: snap.docs[0].id, ...snap.docs[0].data() };
          const ref = await addDoc(collection(db, COL.chats), safe({
            appointmentId, clientId, professionalId,
            createdAt:     realServerTimestamp(),
            lastMessageAt: realServerTimestamp(),
          }));
          return { id: ref.id, appointmentId, clientId, professionalId };
        },

        sendMessage: async (chatId, { senderId, conteudo }) => {
          await addDoc(collection(db, COL.chats, chatId, 'mensagens'), safe({
            senderId, conteudo,
            lida:     false,
            criadaEm: realServerTimestamp(),
          }));
          // Atualiza lastMessageAt no chat pai
          await setDoc(doc(db, COL.chats, chatId), { lastMessageAt: realServerTimestamp() }, { merge: true });
        },

        onSnapshotByAppt: (appointmentId, cb, onError) =>
          onSnapshot(
            query(collection(db, COL.chats), where('appointmentId', '==', appointmentId)),
            (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            onError || ((e) => console.error('[Firestore/chats]', e))
          ),

        onSnapshotMsgs: (chatId, cb, onError) =>
          onSnapshot(
            query(
              collection(db, COL.chats, chatId, 'mensagens'),
              orderBy('criadaEm', 'asc')
            ),
            (snap) => cb(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
            onError || ((e) => console.error('[Firestore/mensagens]', e))
          ),
      };
    })();

// ─── PAYMENTS SERVICE (estrutura preparatória — sem gateway) ──────────────────
export const paymentsService = USE_MOCK
  ? {
      create: async (data) => ({ id: 'mock-payment-1', ...data }),
      update: async ()     => {},
    }
  : (function() {
      const { collection, doc, addDoc, updateDoc } = require('firebase/firestore');

      return {
        create: (data) =>
          addDoc(collection(db, COL.payments), safe({
            ...data,
            status:    'pendente',
            createdAt: realServerTimestamp(),
          })),

        update: (id, data) => updateDoc(doc(db, COL.payments, id), safe(data)),
      };
    })();

// ─── SUBSCRIPTION SERVICE ─────────────────────────────────────────────────────
// Controle manual de acesso: Super Admin libera/bloqueia usuários manualmente.
// Sem gateway de pagamento — fluxo 100% manual.
//
// subscriptionStatus: 'ACTIVE' | 'TRIAL' | 'EXPIRED' | 'BLOCKED'
// accessExpiresAt: Timestamp | null  (null = sem expiração, usado para super admin)
export const subscriptionService = USE_MOCK
  ? {
      setAccess: async () => {},
      addDaysTimestamp: (days) => {
        const d = new Date();
        d.setDate(d.getDate() + days);
        return d;
      },
    }
  : (function() {
      const { doc, updateDoc, Timestamp } = require('firebase/firestore');

      const addDaysTimestamp = (days) => {
        const d = new Date();
        d.setDate(d.getDate() + days);
        d.setHours(23, 59, 59, 999);
        return Timestamp.fromDate(d);
      };

      return {
        addDaysTimestamp,

        // Define acesso manual: status + data de expiração
        setAccess: (uid, status, expiresAt) => {
          const data = { subscriptionStatus: status };
          if (expiresAt !== undefined) data.accessExpiresAt = expiresAt;
          return updateDoc(doc(db, COL.users, uid), safe(data));
        },

        // Marca usuário como EXPIRED no Firestore
        markExpired: (uid) =>
          updateDoc(doc(db, COL.users, uid), { subscriptionStatus: 'EXPIRED' }),
      };
    })();

export default app;
